import axios from "axios";
import { API_BASE_URL_NO_API } from "../utils/apiConfig";
import { ProductApiResponse, ProductBarcodeResponse } from "./productsApi";

const getBaseUrl = () => {
  const base = API_BASE_URL_NO_API;
  return base.endsWith("/api") ? base.slice(0, -4) : base;
};

export interface FullBarcodeResponse extends ProductBarcodeResponse {
  product: number;
  product_name: string;
  product_code: string;
  client: number | null;
  created_at: string;
  created_by: number | null;
  created_by_name: string | null;
}

export interface BarcodeLookupResponse {
  product: ProductApiResponse;
  barcode: FullBarcodeResponse;
}

export interface CreateBarcodePayload {
  code: string;
  barcode_type?: string | null;
  source?: string;
  is_primary?: boolean;
  client_id?: number | null;
}

export interface UpdateBarcodePayload {
  is_primary?: boolean;
  source?: string;
}

export const barcodesApi = {
  barcodeLookup: async (code: string): Promise<BarcodeLookupResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/barcode-lookup/?code=${encodeURIComponent(code)}`;
    const response = await axios.get<BarcodeLookupResponse>(url);
    return response.data;
  },

  getProductBarcodes: async (productId: number): Promise<FullBarcodeResponse[]> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${productId}/barcodes/`;
    const response = await axios.get<FullBarcodeResponse[]>(url);
    return response.data;
  },

  addBarcode: async (
    productId: number,
    data: CreateBarcodePayload
  ): Promise<FullBarcodeResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${productId}/barcodes/`;
    const response = await axios.post<FullBarcodeResponse>(url, data);
    return response.data;
  },

  updateBarcode: async (
    barcodeId: number,
    data: UpdateBarcodePayload
  ): Promise<FullBarcodeResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/barcodes/${barcodeId}/`;
    const response = await axios.patch<FullBarcodeResponse>(url, data);
    return response.data;
  },

  deleteBarcode: async (barcodeId: number): Promise<void> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/barcodes/${barcodeId}/`;
    await axios.delete(url);
  },

  generateBarcode: async (
    productId: number,
    useProductCode?: boolean
  ): Promise<FullBarcodeResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${productId}/barcodes/generate/`;
    const body = useProductCode ? { use_product_code: true } : {};
    const response = await axios.post<FullBarcodeResponse>(url, body);
    return response.data;
  },

  downloadLabel: async (
    barcodeId: number,
    format: "pdf" | "zpl" = "pdf"
  ): Promise<void> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/barcodes/${barcodeId}/label/?label_format=${format}`;
    const response = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([response.data], {
      type: format === "pdf" ? "application/pdf" : "text/plain",
    });
    const objectUrl = URL.createObjectURL(blob);
    if (format === "pdf") {
      window.open(objectUrl, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `label.${format}`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  },
};
